import * as yup from "yup";

import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
  FILE_ALREADY_EXISTS,
} from "./../../../utils/constants";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";

import Datepicker from "../../../components/common/Datepicker";
import Textbox from "../../../components/common/Textbox";
import FileUpload from "../../../components/common/FileUpload";
import Toaster from "./../../../components/common/Toaster";

import complianceService from "./../../../services/complianceService";
import moment from "moment";

const CorItemForm = ({ corDeadline, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fileExists, setFileExists] = useState(false);
  const [filingDate, setFilingDate] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [file, setFile] = useState(null);

  const schema = yup.object({
    amount: yup.string().required("Please enter amount."),
    filing_date: yup.string().required("Please select filing date."),
    payment_date: yup.string().required("Please select payment date."),
    files: yup.mixed(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setIsLoading(true);
      const cor_item = { ...info };
      cor_item["certificate_of_registration_deadline_id"] = corDeadline.id;
      cor_item["certificate_of_registration_id"] =
        corDeadline.certificate_of_registration_id;

      const result = await complianceService.saveCORItemAsync(cor_item);

      const deadline = { ...corDeadline };
      deadline["certificate_of_registration_item_id"] = result.id;
      deadline["filing_date"] = result.filing_date;
      deadline["payment_date"] = result.payment_date;
      deadline["status"] = 2;
      deadline["status_type"] = "Filed";
      deadline["amount"] = info.amount;

      if (result.s3_reference_names && result.s3_reference_names.length > 0) {
        deadline["s3_reference_name"] = result.s3_reference_names[0];
        deadline["document_name"] = result.files[0].file_name;
      } else {
        deadline["s3_reference_name"] = null;
        deadline["document_name"] = null;
      }

      Toaster.Success("File Saved Successfully");

      setIsLoading(false);
      onSuccess(deadline);
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
        setIsLoading(false);
        return;
      }

      console.log(ex.response);

      Toaster.Error("Something went wrong.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchFile = async (url, fileName) => {
      const s3_file = await fetch(url).then((response) => {
        const content_type = response.headers.get("Content-Type");
        const blob = response.blob;

        return new File([blob], fileName, {
          type: content_type,
        });
      });

      s3_file["preview"] = url;

      const files = [];
      files.push(s3_file);

      setValue("files", files);
      setFile(files);
    };

    if (corDeadline) {
      const {
        certificate_of_registration_item_id: id,
        amount,
        filing_date,
        payment_date,
        s3_reference_name,
        document_name,
      } = corDeadline;

      if (id) {
        const formatted_filing_date = moment(filing_date).format("MM-DD-yyyy");
        const formatted_payment_date =
          moment(payment_date).format("MM-DD-yyyy");

        setValue("certificate_of_registration_item_id", id);
        setValue("amount", amount);
        setValue("filing_date", formatted_filing_date);
        setValue("payment_date", formatted_payment_date);

        if (s3_reference_name) fetchFile(s3_reference_name, document_name);

        setFilingDate(formatted_filing_date);
        setPaymentDate(formatted_payment_date);
      }
    }
  }, [corDeadline, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="amount-textbox"
            name="amount"
            type="text"
            title="Amount"
            placeholder="0.00"
            register={register("amount")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Datepicker
            id="filing-date-datepicker"
            name="filing_date"
            title="Date of Filing"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("filing_date")}
            timeFormat={false}
            onDateChange={(date) => setFilingDate(date)}
            errors={errors}
            value={filingDate}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Datepicker
            id="payment-date-datepicker"
            name="payment_date"
            title="Payment Date"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("payment_date")}
            timeFormat={false}
            onDateChange={(date) => setPaymentDate(date)}
            errors={errors}
            value={paymentDate}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <FileUpload
            id="files-dropdown"
            name="files"
            title="Files"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("files")}
            initialValue={file}
            errors={errors}
            accepts={MULTIMEDIA_MIME_TYPES}
            placeholder={MULTIMEDIA_PLACEHOLDER}
            maxFiles={1}
            maxSize={5242880}
            multiple={false}
          />
          {fileExists && (
            <div className="pt-2">
              <span className="text-sm text-red-400">
                {FILE_ALREADY_EXISTS}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default CorItemForm;
